
import { useState, useEffect, useRef } from 'react';

// Check for browser support and handle vendor prefixes
const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

export const useSpeechRecognition = () => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const recognitionRef = useRef<any>(null);

    useEffect(() => {
        if (SpeechRecognition && !recognitionRef.current) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';
            recognitionRef.current = recognition;
        }
    }, []);

    const startListening = () => {
        if (!recognitionRef.current) {
            console.error("Speech recognition not supported.");
            return;
        }
        if (isListening) return;

        try {
            setTranscript(''); // Clear previous session
            recognitionRef.current.start();
            setIsListening(true);
        } catch (error) {
            console.error("Failed to start speech recognition:", error);
        }
    };

    const stopListening = () => {
        if (!recognitionRef.current || !isListening) return;
        try {
            recognitionRef.current.stop();
            setIsListening(false);
        } catch (error) {
            console.error("Failed to stop speech recognition:", error);
        }
    };

    useEffect(() => {
        const recognition = recognitionRef.current;
        if (!recognition) return;

        const handleResult = (event: any) => {
            let finalTranscript = '';
            let interimTranscript = '';

            // Rebuild transcript from scratch based on current session results
            // This prevents the "duplication" bug where text gets appended repeatedly
            for (let i = 0; i < event.results.length; ++i) {
                const result = event.results[i];
                if (result.isFinal) {
                    finalTranscript += result[0].transcript;
                } else {
                    interimTranscript += result[0].transcript;
                }
            }
            
            // Combine confirmed text with currently spoken text
            setTranscript(finalTranscript + interimTranscript);
        };
        
        const handleEnd = () => {
             setIsListening(false);
        };

        const handleError = (event: any) => {
            console.warn('Speech recognition error:', event.error);
            setIsListening(false);
        };

        recognition.onresult = handleResult;
        recognition.onend = handleEnd;
        recognition.onerror = handleError;

        return () => {
            recognition.onresult = null;
            recognition.onend = null;
            recognition.onerror = null;
        };
    }, [recognitionRef.current]);

    return {
        isListening,
        transcript,
        startListening,
        stopListening,
        hasRecognitionSupport: !!recognitionRef.current,
    };
};
